import { useEffect, useState } from 'react';
import { useChatbot } from '../contexts/ChatbotContext';
import { supabase } from '../lib/supabase';
import type { Conversation, ConversationMessage } from '../types/conversations';
import type { ContactInfo } from '../utils/contactInfoDetection';

// Extended conversation type that properly merges database response with UI needs
interface ConversationWithContactInfo extends Omit<Conversation, 'messages'> {
  // Use proper type for messages instead of any[]
  messages?: ConversationMessage[];
  // Add contact_info field
  contact_info?: ContactInfo[];
  // Allow for string|null on initial_message_content to match database response
  initial_message_content?: string | null;
  // Index signature for other properties that might come from the database
  [key: string]: unknown;
}

export function useConversations() {
  const { chatbotPublicId, isLoading: isChatbotLoading } = useChatbot();
  const [conversations, setConversations] = useState<ConversationWithContactInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<ConversationWithContactInfo | null>(null);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!chatbotPublicId) {
        setError('No chatbot selected');
        setLoading(false);
        return;
      }

      // Récupérer d'abord les conversations
      const { data: conversationsData, error: conversationsError } = await supabase
        .from('conversations')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('created_at', { ascending: false });

      if (conversationsError) throw conversationsError;

      // Pour chaque conversation, récupérer les messages et les contacts
      const fullConversations = await Promise.all((conversationsData || []).map(async (conv) => {
        const { data: messages } = await supabase
          .from('conversation_messages')
          .select('*')
          .eq('conversation_id', conv.id)
          .order('created_at', { ascending: true });

        const { data: contacts } = await supabase
          .from('conversation_contact_info')
          .select('*')
          .eq('conversation_id', conv.id);

        // Create conversation object with properly typed fields
        return {
          ...conv,
          // Cast the messages array to the correct type
          messages: (messages || []).map(msg => ({
            id: msg.id,
            conversation_id: msg.conversation_id,
            role: (msg.role as 'user' | 'bot'),
            content: msg.content,
            created_at: msg.created_at,
            sequence: msg.sequence || undefined
          })) as ConversationMessage[],
          // Map contacts to ContactInfo type
          contact_info: contacts?.map(contact => ({
            type: contact.type as 'email' | 'phone',
            value: contact.value,
            messageId: contact.message_id || ''
          })) || []
        } as ConversationWithContactInfo;
      }));

      setConversations(fullConversations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading conversations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isChatbotLoading) {
      fetchConversations();
    }
  }, [chatbotPublicId, isChatbotLoading]);

  const selectConversation = (id: string) => {
    const conversation = conversations.find(conv => conv.id === id);
    setSelectedConversation(conversation || null);
  };

  const deleteConversation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('conversations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setConversations(prev => prev.filter(conv => conv.id !== id));
      if (selectedConversation?.id === id) {
        setSelectedConversation(null);
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
      throw err;
    }
  };

  const deleteAllConversations = async () => {
    try {
      if (!chatbotPublicId) {
        throw new Error('No chatbot selected');
      }

      const { error } = await supabase
        .from('conversations')
        .delete()
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      setConversations([]);
      setSelectedConversation(null);
    } catch (err) {
      console.error('Error deleting all conversations:', err);
      throw err;
    }
  };

  return {
    conversations,
    loading,
    error,
    selectedConversation,
    selectConversation,
    deleteConversation,
    deleteAllConversations
  };
}